import { formatDistanceToNow } from 'date-fns';
import { CommentWithUser } from '@/types/comments';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import AvatarContainer from '@/assets/images/avatars/avatar_container.png';
import { CustomDropdownMenu } from '@/components/ui/CustomDropdownMenu';
import { Link } from 'react-router-dom';
import { FramedAvatar } from '@/components/ui/FramedAvatar';

interface CommentItemProps {
  comment: CommentWithUser;
  className?: string;
  onDelete?: (commentId: string) => void;
}

// Função utilitária para formato curto de tempo
function formatShortDistance(date: Date | number): string {
  const now = new Date();
  const diff = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo`;
  return `${Math.floor(diff / 31536000)}y`;
}

export function CommentItem({ comment, className, onDelete }: CommentItemProps) {
  const { user } = useAuth();
  const isOwnComment = user?.id === comment.user_id;

  return (
    <div className={cn("flex space-x-3 py-3", className)}>
      {/* User Avatar */}
      <Link to={`/profile/${comment.username}`} className="relative w-8 h-8 flex-shrink-0 group">
        <FramedAvatar
          src={comment.user_avatar || undefined}
          initials={comment.user_name ? comment.user_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'A'}
          beltRank={comment.belt_rank || 'white'}
          size={32}
          frameSize={{ w: 32, h: 36 }}
          className=""
        />
      </Link>

      {/* Comment Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2 mb-1">
          <div className="flex items-center flex-1 min-w-0 gap-2">
            <Link to={`/profile/${comment.username}`} className="font-semibold text-white text-sm truncate max-w-[100px] group-hover:underline">
            {comment.user_name || 'Anonymous'}
            </Link>
            <span className="text-xs text-gray-400 truncate max-w-[80px]">
              • {formatShortDistance(new Date(comment.created_at))}
          </span>
          </div>
          {/* 3-dot menu only for own comment */}
          {isOwnComment && (
            <div className="ml-auto">
              <CustomDropdownMenu
                trigger={
                  <span className="ml-2 p-1 rounded-full focus:outline-none cursor-pointer transition-colors inline-flex">
                    <MoreHorizontal className="h-4 w-4 text-gray-400" />
                  </span>
                }
                items={[{
                  label: "Delete comment",
                  icon: <Trash2 className="h-4 w-4 mr-2" />,
                  onClick: () => onDelete?.(comment.id),
                  className: "text-white"
                }]}
              />
            </div>
          )}
        </div>
        <p className="text-gray-100 text-sm leading-relaxed break-words">
          {comment.content}
        </p>
      </div>
    </div>
  );
} 